import React, { ReactElement, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { Tabs } from '@tovia/man-ui';
import { Props as TabProps } from '@tovia/man-ui/lib/components/Tab/Tab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { useMeasure } from 'react-use';

const ARROW_WIDTH = 30;

type Props = {
  className?: string;
  inline?: boolean;
  flat?: boolean;
  children: ReactElement<TabProps>[] | ReactElement<TabProps>;
};

export const AdvancedTabs = (props: Props) => {
  const { children, className, flat, inline } = props;
  const [containerRef, { width }] = useMeasure<HTMLDivElement>();
  const scrollingRef = useRef<number>(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const tabsWidth = Object.values(tabsRef.current?.childNodes || []).reduce((acc, tab) => {
    if (tab.nodeName === 'svg') return acc;
    return acc + (tab as HTMLAnchorElement).offsetWidth;
  }, 0);

  const conatinerWidth = width;
  const showArrows = tabsWidth !== 0 && conatinerWidth !== 0 && tabsWidth > conatinerWidth;

  const handleScroll = (scrollOffset: number) => () => {
    clearInterval(scrollingRef.current);
    if (wrapperRef.current) {
      const tabsDiv = wrapperRef.current;
      scrollingRef.current = window.setInterval(() => {
        const { scrollLeft } = tabsDiv;
        tabsDiv.scrollLeft = scrollLeft + scrollOffset;
      }, 1);
    }
  };

  const stopScroll = () => clearInterval(scrollingRef.current);

  return (
    <TabsContainer className={className} ref={containerRef}>
      <Arrow
        $visible={showArrows}
        $side="left"
        onMouseDown={handleScroll(-3)}
        onTouchStart={handleScroll(-3)}
        onTouchEnd={stopScroll}
        onMouseUp={stopScroll}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </Arrow>
      <TabsWrapper ref={wrapperRef} onTouchStart={stopScroll}>
        <StyledTabs ref={tabsRef} flat={flat} inline={inline} width={tabsWidth} $arrowsVisible={showArrows}>
          {children}
        </StyledTabs>
      </TabsWrapper>
      <Arrow
        $visible={showArrows}
        $side="right"
        onMouseDown={handleScroll(3)}
        onTouchStart={handleScroll(3)}
        onTouchEnd={stopScroll}
        onMouseUp={stopScroll}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </Arrow>
    </TabsContainer>
  );
};

const TabsContainer = styled.div`
  user-select: none;
  position: relative;
  overflow: hidden;
`;

const Arrow = styled.div<{ $side: 'left' | 'right'; $visible: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  ${(props) => props.$side}: 0;
  top: 0;
  height: 100%;
  font-size: 1rem;
  display: ${(props) => (props.$visible ? 'flex' : 'none')};
  opacity: 80%;
  width: ${ARROW_WIDTH}px;
  cursor: pointer;
  background: ${(props) => props.theme.primary6};
  border-bottom: 1px solid ${(props) => props.theme.primary3} !important;

  &:hover {
    opacity: 100%;
  }
`;

const TabsWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;

  /* Hide the scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledTabs = styled(Tabs)<{ width?: number; $arrowsVisible: boolean }>`
  white-space: nowrap;
  ${(props) =>
    props.width &&
    props.$arrowsVisible &&
    css`
      width: ${props.width + ARROW_WIDTH * 2}px;
      padding: 0 ${ARROW_WIDTH}px;
    `}
`;
